/**
 * usePatrolAnimation — billiard-ball physics mascot.
 *
 * The mascot moves freely across the viewport, bouncing off:
 *   - Viewport edges
 *   - DOM elements (header, sidebar, kanban columns, cards)
 *
 * Speed varies organically via layered sine waves.
 * Each bounce adds slight angle jitter so the path never repeats.
 *
 * Performance: writes position directly to the DOM via ref —
 * no React re-renders in the hot loop.
 */

import { useRef, useEffect, useCallback, useState } from 'react';

// --- Tuning ---
const BASE_SPEED = 0.028; // px per ms at 1× multiplier
const MASCOT_SIZE = 20;
const OBSTACLE_SCAN_INTERVAL_MS = 800;
const BOUNCE_JITTER_RAD = 0.2; // ±~11° randomness on bounce
const MIN_BOUNCE_COOLDOWN_MS = 200;
const FLEE_RADIUS = 130; // px — mascot starts fleeing when mouse this close
const BUMP_RADIUS = 42; // px — triggers bounce impulse on near contact
const FLEE_SPEED_BOOST = 2.8; // max speed multiplier from mouse proximity
const BUMP_COOLDOWN_MS = 350; // ms between bump impulses

/** Selectors for DOM elements the mascot bounces off */
const OBSTACLE_SELECTORS = [
  'header',
  'aside',
  'nav',
  'div.flex-shrink-0.transition-collapse',
  '.collapsed-column-hover',
  '.group.relative.rounded-xl',
].join(',');

// --- Types ---

export interface Vec2 {
  x: number;
  y: number;
}

interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

// --- Helpers ---

function speedMultiplier(now: number): number {
  const s1 = Math.sin(now / 8_000);
  const s2 = Math.sin(now / 3_400 + 1.7);
  const s3 = Math.sin(now / 13_000 + 4.2);
  const combined = (s1 + s2 * 0.7 + s3 * 0.7) / 2.4;
  const normalised = (combined + 1) / 2;
  return 0.3 + normalised * 1.4; // 0.3–1.7×
}

function randomAngle(): number {
  const angle = Math.random() * Math.PI * 2;
  const ax = Math.abs(Math.cos(angle));
  const ay = Math.abs(Math.sin(angle));
  if (ax < 0.15 || ay < 0.15) return randomAngle();
  return angle;
}

function jitter(): number {
  return (Math.random() - 0.5) * 2 * BOUNCE_JITTER_RAD;
}

function rotateVec(v: Vec2, rad: number): Vec2 {
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return { x: v.x * cos - v.y * sin, y: v.x * sin + v.y * cos };
}

function normalise(v: Vec2): Vec2 {
  const mag = Math.sqrt(v.x * v.x + v.y * v.y);
  return mag > 0 ? { x: v.x / mag, y: v.y / mag } : v;
}

function overlaps(mascot: Rect, obstacle: Rect): boolean {
  return (
    mascot.left < obstacle.right &&
    mascot.right > obstacle.left &&
    mascot.top < obstacle.bottom &&
    mascot.bottom > obstacle.top
  );
}

function reflectOff(pos: Vec2, vel: Vec2, obstacle: Rect): Vec2 {
  const cx = pos.x + MASCOT_SIZE / 2;
  const cy = pos.y + MASCOT_SIZE / 2;
  const ox = (obstacle.left + obstacle.right) / 2;
  const oy = (obstacle.top + obstacle.bottom) / 2;
  const dx = cx - ox;
  const dy = cy - oy;
  const hw = (obstacle.right - obstacle.left) / 2 + MASCOT_SIZE / 2;
  const hh = (obstacle.bottom - obstacle.top) / 2 + MASCOT_SIZE / 2;
  const overlapX = hw - Math.abs(dx);
  const overlapY = hh - Math.abs(dy);

  const reflected =
    overlapX < overlapY ? { x: -vel.x, y: vel.y } : { x: vel.x, y: -vel.y };

  return normalise(rotateVec(reflected, jitter()));
}

function pushOut(pos: Vec2, obstacle: Rect): Vec2 {
  const cx = pos.x + MASCOT_SIZE / 2;
  const cy = pos.y + MASCOT_SIZE / 2;
  const ox = (obstacle.left + obstacle.right) / 2;
  const oy = (obstacle.top + obstacle.bottom) / 2;
  const dx = cx - ox;
  const dy = cy - oy;
  const hw = (obstacle.right - obstacle.left) / 2 + MASCOT_SIZE / 2;
  const hh = (obstacle.bottom - obstacle.top) / 2 + MASCOT_SIZE / 2;
  const overlapX = hw - Math.abs(dx);
  const overlapY = hh - Math.abs(dy);

  if (overlapX < overlapY) {
    return { x: pos.x + (dx > 0 ? overlapX : -overlapX), y: pos.y };
  }
  return { x: pos.x, y: pos.y + (dy > 0 ? overlapY : -overlapY) };
}

// --- Hook ---

export function usePatrolAnimation() {
  const elRef = useRef<HTMLSpanElement | null>(null);

  const posRef = useRef<Vec2>({
    x: Math.random() * (window.innerWidth - MASCOT_SIZE),
    y: Math.random() * (window.innerHeight - MASCOT_SIZE),
  });
  const [initAngle] = useState(randomAngle);
  const velRef = useRef<Vec2>({
    x: Math.cos(initAngle),
    y: Math.sin(initAngle),
  });

  const lastFrameRef = useRef(0);
  const lastBounceTimeRef = useRef(0);
  const obstaclesRef = useRef<Rect[]>([]);
  const lastObstacleScanRef = useRef(0);
  const rafRef = useRef(0);
  const viewportRef = useRef({ w: window.innerWidth, h: window.innerHeight });

  // Exposed physics refs for transform pipeline
  const speedRef = useRef(0);
  const lastBounceRef = useRef(0);
  const bounceNormalRef = useRef<Vec2>({ x: 0, y: 0 });

  // Mouse flee state
  const mouseRef = useRef<Vec2>({ x: -9999, y: -9999 });
  const fleeStrengthRef = useRef(0);
  const lastBumpRef = useRef(0);

  const scanObstacles = useCallback(() => {
    const els = document.querySelectorAll(OBSTACLE_SELECTORS);
    const rects: Rect[] = [];
    els.forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.width < 20 || r.height < 20) return;
      rects.push({
        left: r.left,
        top: r.top,
        right: r.right,
        bottom: r.bottom,
      });
    });
    obstaclesRef.current = rects;
  }, []);

  useEffect(() => {
    const onResize = () => {
      viewportRef.current = { w: window.innerWidth, h: window.innerHeight };
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', onMouseMove);
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, []);

  useEffect(() => {
    let visible = !document.hidden;

    const onVisibility = () => {
      visible = !document.hidden;
      if (visible) lastFrameRef.current = performance.now();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const tick = (now: number) => {
      rafRef.current = requestAnimationFrame(tick);
      if (!visible) return;

      if (!lastFrameRef.current) {
        lastFrameRef.current = now;
        scanObstacles();
        return;
      }

      const dt = Math.min(now - lastFrameRef.current, 50); // cap delta to avoid jumps
      lastFrameRef.current = now;

      // Periodic obstacle scan
      if (now - lastObstacleScanRef.current > OBSTACLE_SCAN_INTERVAL_MS) {
        lastObstacleScanRef.current = now;
        scanObstacles();
      }

      // Move
      const speed = BASE_SPEED * speedMultiplier(now);
      speedRef.current = (speedMultiplier(now) - 0.3) / 1.4; // normalized 0-1
      const vel = velRef.current;
      const pos = posRef.current;

      // Mouse flee: steer away and boost speed when mouse is near
      const mascotCx = pos.x + MASCOT_SIZE / 2;
      const mascotCy = pos.y + MASCOT_SIZE / 2;
      const mdx = mascotCx - mouseRef.current.x;
      const mdy = mascotCy - mouseRef.current.y;
      const mouseDist = Math.sqrt(mdx * mdx + mdy * mdy);
      let moveSpeed = speed;

      if (mouseDist < FLEE_RADIUS && mouseDist > 1) {
        const proximity = 1 - mouseDist / FLEE_RADIUS;
        fleeStrengthRef.current = proximity;
        const fleeDir = normalise({ x: mdx, y: mdy });
        const steer = Math.min(0.3, proximity * 0.35);
        velRef.current = normalise({
          x: vel.x * (1 - steer) + fleeDir.x * steer,
          y: vel.y * (1 - steer) + fleeDir.y * steer,
        });
        moveSpeed = speed * (1 + proximity * FLEE_SPEED_BOOST);
        speedRef.current = Math.min(1, speedRef.current + proximity * 0.8);

        // Bump impulse: strong push on near contact
        if (
          mouseDist < BUMP_RADIUS &&
          now - lastBumpRef.current > BUMP_COOLDOWN_MS
        ) {
          lastBumpRef.current = now;
          lastBounceRef.current = now; // triggers bounce squash visuals
          velRef.current = normalise(rotateVec(fleeDir, jitter()));
        }
      } else {
        fleeStrengthRef.current = Math.max(0, fleeStrengthRef.current - 0.04);
      }

      pos.x += velRef.current.x * moveSpeed * dt;
      pos.y += velRef.current.y * moveSpeed * dt;

      const { w, h } = viewportRef.current;
      let bounced = false;

      // Viewport edge bounces
      if (pos.x <= 0) {
        pos.x = 0;
        velRef.current = normalise(
          rotateVec(
            { x: Math.abs(velRef.current.x), y: velRef.current.y },
            jitter()
          )
        );
        bounced = true;
      } else if (pos.x >= w - MASCOT_SIZE) {
        pos.x = w - MASCOT_SIZE;
        velRef.current = normalise(
          rotateVec(
            { x: -Math.abs(velRef.current.x), y: velRef.current.y },
            jitter()
          )
        );
        bounced = true;
      }
      if (pos.y <= 0) {
        pos.y = 0;
        velRef.current = normalise(
          rotateVec(
            { x: velRef.current.x, y: Math.abs(velRef.current.y) },
            jitter()
          )
        );
        bounced = true;
      } else if (pos.y >= h - MASCOT_SIZE) {
        pos.y = h - MASCOT_SIZE;
        velRef.current = normalise(
          rotateVec(
            { x: velRef.current.x, y: -Math.abs(velRef.current.y) },
            jitter()
          )
        );
        bounced = true;
      }

      // DOM obstacle bounces
      if (
        !bounced &&
        now - lastBounceTimeRef.current > MIN_BOUNCE_COOLDOWN_MS
      ) {
        const mascotRect: Rect = {
          left: pos.x,
          top: pos.y,
          right: pos.x + MASCOT_SIZE,
          bottom: pos.y + MASCOT_SIZE,
        };

        for (const obs of obstaclesRef.current) {
          if (overlaps(mascotRect, obs)) {
            velRef.current = reflectOff(pos, velRef.current, obs);
            const pushed = pushOut(pos, obs);
            pos.x = pushed.x;
            pos.y = pushed.y;
            bounced = true;
            break;
          }
        }
      }

      if (bounced) {
        lastBounceTimeRef.current = now;
        lastBounceRef.current = now;
        bounceNormalRef.current = normalise(vel);
      }

      // Write directly to DOM — no React re-render
      const el = elRef.current;
      if (el) {
        el.style.left = `${pos.x}px`;
        el.style.top = `${pos.y}px`;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [scanObstacles]);

  const reverseDirection = useCallback(() => {
    const v = velRef.current;
    velRef.current = normalise(rotateVec({ x: -v.x, y: -v.y }, jitter()));
  }, []);

  return {
    elRef,
    reverseDirection,
    velocityRef: velRef,
    speedRef,
    lastBounceRef,
    bounceNormalRef,
    fleeStrengthRef,
  };
}
